import { debounce } from "../../../../core/helpers/functional-util.js";
import { Node } from "prosemirror-model";
import { NodeSelection } from "prosemirror-state";
import AbstractAction from "../AbstractAction.js";
import inputValueConvertor from "./inputValueConvertor.js";

/**
 * Editor action to provide support for <input-value> tags.
 * Parsing such a tag will trigger an async call to retrieve the value associated,
 * and then inputValueConvertor will display the resolved value.
 * Once the value is resolved, `inputValueConvertor.onLoad` is triggered with the resolved
 * value, and in result alters the state of the editor. As any other state alterations,
 * this ultimately triggers a `WysiwygEditor.onChange` event, so that the server
 * can store the new value.
 *
 * @param {string} type - Type of input value to handle. This will be matched
 *   against the `data-type` attribute of the HTML node
 * @param {function} fetcher - Async function used to retrieve the value
 *   from the HTML tag attributes. The promise must resolve to an object.
 *   This object will later be passed to `getValue` and `getTitle`.
 * @param {string[]} [attributes] - List of supported attributes for the provided type.
 *   By example, passing ["id"] would allow the attribute `data-id` to be set on the node,
 *   and the attribute `id` will be passed to the fetcher
 * @param {{[key: string]: string}} [optionalAttributes] - Object holding the optional attributes
 *   supported by the input-value. The key is the attribute name, and the value is the default
 *   value for the attribute
 * @param {function(object): void} [makePopover] - Function taking the fetched object as parameter,
 *   as well as the DOM node triggering the popover on click
 * @param {function(object):string} [getValue] - Function taking the fetched object as parameter
 *   and returning the value to inject in the editor
 * @param {function(object):string} [getTitle] - Function taking the fetched object as parameter
 *   and returning the title of the editor node. If not provided, no title will be shown
 */
export default class InputValueAction extends AbstractAction {
	constructor({
		type,
		fetcher,
		attributes = [],
		optionalAttributes = {},
		makePopover,
		getValue = (fetched) => fetched.toString(),
		getTitle,
	}) {
		super(...arguments);

		this._fetcher = fetcher;
		this._type = type;
		this._attributes = attributes;
		this._optionalAttributes = optionalAttributes;
		this._makePopover = makePopover;
		this._getValue = getValue;
		this._getTitle = getTitle;
	}

	getNodeViews() {
		return {
			[`inputValue_${this._type}`]: (node, view, getPosition) =>
				this._makeNodeView({ node, view, getPosition }),
		};
	}

	getNodes() {
		return {
			[`inputValue_${this._type}`]: {
				group: "inline",
				inline: true,
				atom: true,
				content: "text*",
				attrs: this._schemaAttributes(),
				parseDOM: [
					{
						tag: `input-value[data-type="${this._type}"]`,
						getAttrs: this._getAttributesFromDOM.bind(this),
					},
				],
				toDOM: this._toDOMSpecification.bind(this),
			},
		};
	}

	_toDOMSpecification(node) {
		const allDataAttributes = Object.keys(node.attrs).reduce(
			(result, name) => {
				result[`data-${name}`] = node.attrs[name];
				return result;
			},
			{ "data-type": this._type },
		);

		return ["input-value", allDataAttributes, 0];
	}

	_getAttributesFromDOM(dom) {
		const allAttributes = [
			...this._attributes,
			...Object.keys(this._optionalAttributes),
		];

		return allAttributes.reduce((acc, attribute) => {
			acc[attribute] = dom.getAttribute(`data-${attribute}`);

			return acc;
		}, {});
	}

	_schemaAttributes() {
		const allAttributes = this._attributes.reduce((acc, attribute) => {
			acc[attribute] = {};

			return acc;
		}, {});

		return Object.keys(this._optionalAttributes).reduce(
			(acc, optionalAttribute) => {
				acc[optionalAttribute] = {
					default: this._optionalAttributes[optionalAttribute],
				};

				return acc;
			},
			allAttributes,
		);
	}

	_makeNodeView({ node: rawNode, view, getPosition }) {
		const node = this._cleanNode(rawNode);
		const dom = document.createElement("span");

		this._fetchValue({
			view,
			getPosition,
			node,
			dom,
		});

		return {
			update: this._updateNode({
				node,
				view,
				getPosition,
				dom,
			}),
			dom,
		};
	}

	/**
	 * prosemirror's raw nodes are using booleans as strings
	 */
	_cleanNode(node) {
		Object.entries(node.attrs).forEach(([attr, value]) => {
			if (value === "true") node.attrs[attr] = true;
			if (value === "false") node.attrs[attr] = false;
		});

		return node;
	}

	_updateNode({ node, view, getPosition, dom }) {
		return (newNode) => {
			if (newNode.type.name !== node.type.name) {
				return false;
			}

			if (
				this._areAttributesEqual({
					oldNode: node,
					newNode,
				})
			) {
				return true;
			}

			this._fetchValue({
				view,
				getPosition,
				node: newNode,
				dom,
			});

			return true;
		};
	}

	/**
	 * Compare the attributes of two different versions of a node.
	 * Since the nodes are of same type, they have the same attributes,
	 * so there is no need to check that one has more keys that the other.
	 *
	 * @param {ProseMirror.Node} oldNode - Node to compare
	 * @param {ProseMirror.Node} newNode - Node to compare
	 * @return {boolean} True when the attributes haven't change
	 */
	_areAttributesEqual({ oldNode, newNode }) {
		return Object.keys(oldNode.attrs).every(
			(name) => oldNode.attrs[name] === newNode.attrs[name],
		);
	}

	_fetchValue({ view, getPosition, node, dom }) {
		inputValueConvertor({
			type: this._type,
			updateNodeAttributes: debounce({
				fn: (newAttributes) => {
					const allAttributes = { ...node.attrs, ...newAttributes };

					this._updateNodeAttributes({
						view,
						getPosition,
						type: node.type,
						newAttributes: allAttributes,
					});

					// This has to be done after the update for the new fetch to occur
					node.attrs = allAttributes;
				},
			}),
			container: dom,
			node,
			getValue: this._getValue,
			getTitle: this._getTitle,
			fetcher: () => this._fetcher(node.attrs),
			makePopover: this._makePopover,
			onLoad: (content) => {
				this._updateEditorState({
					content,
					getPosition,
					node,
					view,
				});
			},
		});
	}

	_updateNodeAttributes({ view, getPosition, type, newAttributes }) {
		const position = getPosition();
		const transaction = view.state.tr.setNodeMarkup(
			position,
			type,
			newAttributes,
		);

		view.dispatch(transaction);
	}

	_updateEditorState({ view, getPosition, node, content }) {
		const transaction = view.state.tr;

		const newNode = Node.fromJSON(view.state.config.schema, {
			type: `inputValue_${this._type}`,
			attrs: { ...node.attrs },
			content: [
				{
					type: "text",
					text: content,
				},
			],
		});

		// We use `NodeSelection` to select the node to replace
		// By using this, we don't have to deal manually with position
		// which is a nightmare, and have revealed to be really complicated
		const selection = NodeSelection.create(view.state.doc, getPosition());
		selection.replaceWith(transaction, newNode);
		view.dispatch(transaction);
	}
}
